import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cibli/shared';
import { useMicdropState } from '@micdrop/react';
import { Micdrop } from '@micdrop/web';
import { AnimatePresence, motion } from 'framer-motion';
import { Link2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from '@/features/common/components/ConfirmDialog';
import { useResetToHome } from '@/features/common/hooks/useResetToHome';

export default function WSReconnecting() {
  const { isReconnecting } = useMicdropState();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { resetToHome } = useResetToHome();

  // Rotating subtitles loaded from i18n translations
  const subtitles = useMemo<string[]>(
    () => (t('Interview.WSReconnecting.subtitles', { returnObjects: true }) as string[]) || [],
    [t],
  );

  const [subtitleIndex, setSubtitleIndex] = useState<number>(0);

  // Cycle subtitle every ~7s while the banner is visible
  useEffect(() => {
    if (!isReconnecting) {
      setSubtitleIndex(0);
      return;
    }
    const id = setInterval(() => {
      setSubtitleIndex(prev => (prev + 1) % subtitles.length);
    }, 7000);
    return () => clearInterval(id);
  }, [isReconnecting, subtitles.length]);

  return (
    <Dialog open={isReconnecting} onOpenChange={() => {}}>
      <DialogContent className="sm:min-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            <span className="flex items-start gap-4 text-start">
              <span className="relative inline-block h-12 w-12" aria-hidden>
                <div className="absolute inset-0 z-10 grid place-items-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/30">
                  <Link2 className="h-5 w-5" />
                </div>
                <motion.div
                  className="absolute -inset-[3px] grid place-items-center"
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, ease: 'linear', duration: 1.1 }}
                >
                  <div className="h-full w-full rounded-full border-7 border-primary border-t-transparent" />
                </motion.div>
              </span>
              {t('Interview.WSReconnecting.title')}
            </span>
          </DialogTitle>
          <DialogDescription className="text-lg min-h-[1.5rem] text-start" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.div
                key={subtitleIndex}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.35 }}
              >
                {subtitles[subtitleIndex]}
              </motion.div>
            </AnimatePresence>
          </DialogDescription>
        </DialogHeader>

        <DialogDescription>
          <p id="ws-reconnecting-return-home-description" className="text-sm leading-snug">
            {t('Interview.WSReconnecting.returnHomeDescription', {
              defaultValue: 'You can return to the home page if the reconnection fails.',
            })}
          </p>
        </DialogDescription>

        <DialogFooter>
          <Button
            size="lg"
            variant="outline"
            aria-describedby="ws-reconnecting-return-home-description"
            onClick={() => setConfirmOpen(true)}
          >
            {t('Interview.WSReconnecting.returnHome', { defaultValue: 'Return home' })}
          </Button>
        </DialogFooter>

        {/* Confirm return home dialog */}
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('Interview.WSReconnecting.confirmDialog.title', {
            defaultValue: 'Return to home?',
          })}
          description={t('Interview.WSReconnecting.confirmDialog.description', {
            defaultValue:
              'You will leave the interview and be redirected to the home screen. This action cannot be undone.',
          })}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('common.cancel')}
          onConfirm={() => {
            Micdrop.stop();
            resetToHome();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
